/**
 * Date validation and parsing utilities
 */

/**
 * Validates if a value is a valid Date object
 * @param value - Any value to check
 * @returns True if value is a valid Date, false otherwise
 * @example
 * isValidDate(new Date()) // true
 * isValidDate(new Date("invalid")) // false
 * isValidDate("2024-01-17") // false (string, not Date)
 */
export const isValidDate = (value: any): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

/**
 * Converts various date formats to a Date object, with validation
 * @param value - Date string, Date object, or other value
 * @returns Valid Date object or null if invalid
 * @example
 * getDateValue("2024-01-17") // Date object
 * getDateValue(new Date()) // Date object
 * getDateValue("invalid") // null
 * getDateValue(null) // null
 */
export const getDateValue = (value: any): Date | null => {
  if (value) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  return null;
};

/**
 * Gets the date of Monday for the current week in local time.
 * If today is Sunday, returns the date of the previous Monday.
 *
 * @returns A string representing Monday's date in "YYYY-MM-DD" format
 * @example
 * // If today is Wednesday 2024-01-17
 * getMondayOfCurrentWeek() // Returns "2024-01-15"
 *
 * // If today is Sunday 2024-01-21
 * getMondayOfCurrentWeek() // Returns "2024-01-15"
 */
export const getMondayOfCurrentWeek = (): string => {
  const currentDate = new Date();
  const day = currentDate.getDay();
  const mondayDate = new Date(currentDate);
  mondayDate.setDate(currentDate.getDate() - (day === 0 ? 6 : day - 1));

  const year = mondayDate.getFullYear();
  const month = String(mondayDate.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(mondayDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${dayOfMonth}`;
};

/**
 * Creates a UTC Date object from a "YYYY-MM-DD" string.
 * This is a reliable way to parse date strings into UTC, avoiding
 * browser inconsistencies with `new Date()`.
 *
 * @param dateString The date string in "YYYY-MM-DD" format.
 * @returns A Date object representing midnight UTC on that date.
 * @example
 * dateStringToUtc("2024-01-17") // Returns Date object for 2024-01-17 00:00:00 UTC
 */
export const dateStringToUtc = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Calculates the start of the week (Monday) in UTC for a given date.
 *
 * @param date The input date. If not provided, defaults to the current date.
 * @returns A new Date object set to the preceding Monday at 00:00:00 UTC.
 * @example
 * getUtcWeekStartDate(new Date("2024-01-17")) // Returns Monday of that week in UTC
 */
export const getUtcWeekStartDate = (date: Date = new Date()): Date => {
  // Create a copy to avoid mutating the original date
  const d = new Date(date.valueOf());
  const utcDay = d.getUTCDay(); // Sunday = 0, Monday = 1, etc.

  // Calculate how many days to subtract to get to the previous Monday
  const daysToSubtract = utcDay === 0 ? 6 : utcDay - 1;

  d.setUTCDate(d.getUTCDate() - daysToSubtract);
  d.setUTCHours(0, 0, 0, 0);

  return d;
};

/**
 * Gets the date of Thursday for the current week in local time.
 * Always returns this week's Thursday, regardless of what day today is.
 * This is used as a weekly cutoff - deliveries before this week's Thursday are considered late.
 *
 * @returns A Date object representing Thursday of the current week
 * @example
 * // If today is Wednesday 2024-01-17
 * getThisWeeksThursday() // Returns Thursday 2024-01-18
 *
 * // If today is Friday 2024-01-19  
 * getThisWeeksThursday() // Returns Thursday 2024-01-18 (this week, not next week)
 */
export const getThisWeeksThursday = (): Date => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 4 = Thursday
  
  const thursday = new Date(today);
  
  // Always get this week's Thursday (not next week's)
  thursday.setDate(today.getDate() + (4 - dayOfWeek));
  
  thursday.setHours(0, 0, 0, 0); // Reset time to start of day
  return thursday;
};
